import React, { useState, useContext, useEffect } from 'react';
import LogoutButton from '../Shared/LogoutButton/LogoutButton';
import { Link } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isGlobalAdmin, isClinicLevelAdmin } from '../../utils/permissions';
import GearPopup from './GearPopup';
import IntroGuide from '../IntroGuide/IntroGuide';
import PhraseManager from '../PhraseManager/PhraseManager';
import apiClient from '../../apiClient';
import logo from '../../assets/logo3.png';
import './Sidebar.css';

// Undermeny-komponent med hover och fixed positioning
const SubMenu = ({ icon, label, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0 });
  const triggerRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.top });
    }
    setIsOpen(true);
  };

  return (
    <div
      className="submenu-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button ref={triggerRef} className="submenu-trigger sidebar-link">
        <i className={icon}></i> {label}
        <i className="fas fa-chevron-right submenu-arrow"></i>
      </button>
      {isOpen && (
        <div
          className="submenu-dropdown"
          style={{ top: position.top }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

function Sidebar() {
  const { user } = useContext(UserContext);
  const [isGearPopupOpen, setIsGearPopupOpen] = useState(false);
  const [isIntroGuideOpen, setIsIntroGuideOpen] = useState(false);
  const [useInternalJournal, setUseInternalJournal] = useState(false);
  const [useSportsModule, setUseSportsModule] = useState(false);
  const [isPhraseManagerOpen, setIsPhraseManagerOpen] = useState(false);

  // Hämta klinik-inställningar för att visa/dölja journalmodulen och sportmodulen
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setUseInternalJournal(resp.data.use_internal_journal || false);
        setUseSportsModule(resp.data.use_sports_module || false);
      } catch (error) {
        // Kliniken har inte modulerna aktiverade eller anrop misslyckades
        setUseInternalJournal(false);
        setUseSportsModule(false);
      }
    };

    fetchClinicSettings();
  }, []);

  // Öppna Aion Sports med SSO-token
  const openSportsModule = () => {
    const token = localStorage.getItem('access_token');
    const sportsUrl = process.env.REACT_APP_SPORTS_URL || 'http://localhost:3001';
    window.open(`${sportsUrl}/login?token=${token}`, '_blank');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  };

  const toggleGearPopup = () => {
    setIsGearPopupOpen(!isGearPopupOpen);
  };

  const toggleIntroGuide = () => {
    setIsIntroGuideOpen(!isIntroGuideOpen);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-content">
        {/* Logo och användarinfo */}
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={logo} alt="Aion Notes Logo" className="sidebar-logo" />
          </div>
          <p className="greeting">Inloggad som: {user?.first_name} {user?.last_name}</p>
        </div>

        {/* Huvudnavigation */}
        <nav className="main-navigation">
          <div className="nav-section">
            <Link to="/" className="sidebar-link">
              <i className="fas fa-home"></i> Startsida
            </Link>
          </div>

          {/* Patientmöten */}
          <div className="nav-section">
            <span className="nav-section-label">Patientmöten</span>
            <Link to="/main-content" className="sidebar-link">
              <i className="fas fa-microphone"></i> Nytt patientmöte
            </Link>
            <Link to="/sessions" className="sidebar-link">
              <i className="fas fa-list"></i> Mina patientmöten
            </Link>
          </div>

          {/* Arbetsmöten */}
          <div className="nav-section">
            <span className="nav-section-label">Arbetsmöten</span>
            <Link to="/meeting-content" className="sidebar-link">
              <i className="fas fa-plus-circle"></i> Nytt arbetsmöte
            </Link>
            <Link to="/my-meetings" className="sidebar-link">
              <i className="fas fa-folder-open"></i> Mina arbetsmöten
            </Link>
          </div>

          {/* Aion Sports - visas endast om kliniken har sportmodulen aktiverad */}
          {useSportsModule && (
            <div className="nav-section">
              <span className="nav-section-label">Sportmodul</span>
              <button onClick={openSportsModule} className="sidebar-link sports-link">
                <i className="fas fa-running"></i> Öppna Aion Sports
                <i className="fas fa-external-link-alt sports-external-icon"></i>
              </button>
            </div>
          )}

          {/* Admin-sektion med undermenyer (global admin + klinikadmin) */}
          {isClinicLevelAdmin(user) && (
            <div className="nav-section admin-section">
              <span className="nav-section-label">Administration</span>

              {/* Mallar */}
              <SubMenu icon="fas fa-file-alt" label="Mallar">
                <Link to="/journalmallar" className="submenu-link">
                  <i className="fas fa-notes-medical"></i> Journalmallar
                </Link>
                <Link to="/documentmallar" className="submenu-link">
                  <i className="fas fa-file-signature"></i> Dokumentmallar
                </Link>
                <Link to="/meetingmallar" className="submenu-link">
                  <i className="fas fa-clipboard-list"></i> Mötesmallar
                </Link>
              </SubMenu>

              {/* Användare & Kliniker */}
              <SubMenu icon="fas fa-users-cog" label="Organisation">
                <Link to="/users" className="submenu-link">
                  <i className="fas fa-users"></i> Användare
                </Link>
                {isGlobalAdmin(user) && (
                  <Link to="/clinics" className="submenu-link">
                    <i className="fas fa-hospital"></i> Kliniker
                  </Link>
                )}
              </SubMenu>

              {/* Övervakning & Statistik (endast global admin) */}
              {isGlobalAdmin(user) && (
                <SubMenu icon="fas fa-chart-line" label="Övervakning">
                  <Link to="/usage-dashboard" className="submenu-link">
                    <i className="fas fa-chart-bar"></i> Användningsstatistik
                  </Link>
                  <Link to="/gpu-monitoring" className="submenu-link">
                    <i className="fas fa-microchip"></i> GPU Monitoring
                  </Link>
                  <Link to="/support-tickets" className="submenu-link">
                    <i className="fas fa-ticket-alt"></i> Support Tickets
                  </Link>
                  <Link to="/admin-errors" className="submenu-link">
                    <i className="fas fa-exclamation-triangle"></i> Fel & Retry
                  </Link>
                  <Link to="/medical-terms" className="submenu-link">
                    <i className="fas fa-stethoscope"></i> Medicinska termer
                  </Link>
                </SubMenu>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* Guide-knapp */}
      <div className="guide-section">
        <button onClick={toggleIntroGuide} className="sidebar-link guide-link">
          <i className="fas fa-info-circle"></i> Guide
        </button>
      </div>

      {/* Footer med inställningar och logout */}
      <div className="sidebar-footer">
        <div className="footer-buttons">
          <button
            className="gear-button"
            onClick={toggleGearPopup}
            aria-label="Öppna inställningar"
          >
            <i className="fas fa-cog" aria-hidden="true"></i>
          </button>
          <LogoutButton onLogout={handleLogout} />
        </div>
      </div>

      {isGearPopupOpen && (
        <GearPopup
          onClose={toggleGearPopup}
          onOpenPhraseManager={() => {
            setIsGearPopupOpen(false);
            setIsPhraseManagerOpen(true);
          }}
        />
      )}
      {isIntroGuideOpen && <IntroGuide isOpen={isIntroGuideOpen} onClose={() => setIsIntroGuideOpen(false)} />}
      {isPhraseManagerOpen && <PhraseManager onClose={() => setIsPhraseManagerOpen(false)} />}
    </div>
  );
}

export default Sidebar;
